import React, {Component} from 'react'
import {connect} from 'react-redux'
import propTypes from 'prop-types'
import {get, isEmpty, isNil} from 'lodash'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Switch from '@material-ui/core/Switch'
import iconNames from 'theme/icons'
import PivotTable from 'containers/pivotTable'
import FinancialString from '../../containers/financialString'
import {partsPivotTableKey} from 'reducers/priceListsContext/parts'
import {getPackTypes, getPartTemplates, isComplexPricingEnabled} from 'selectors/priceLists'
import {getCurrencies, getNominalCodes, getTaxCodes} from 'selectors/data'
import {getRegionalLocaleRRPTerm} from 'selectors/settings'
import {getAreAllRowsSelected} from 'selectors/pivotTable'
import {deSelectAllRows, selectAllRows, toggleSelectedEntity} from 'actions/pivotTable'
import {prefix} from 'helpers/component'
import {getTaxCodeOption} from 'helpers/taxCodes'
import {createDateTimeColumn, getColorStatusOption} from 'helpers/pivotTable'
import {getControlColumns} from 'helpers/parts/controlDefinition'
import {getDefaultCurrency} from 'helpers/financial'
import {getMinPriceBreak, getPartPriceBreaks} from 'helpers/priceBreaks'
import {getDefaultPack, getPartPacks} from 'helpers/packs'
import {getPackTypeFor} from 'helpers/packTypes'
import {
  getColorForPartStatus,
  getCostSummaryFor,
  getNominalCodeDescription,
  getPartPropertyWithLengthClass,
  getPartPropertyWithWeightClass,
  getPartStatus,
  getPartStatusLabel,
  getPartType,
  getPriceSummaryFor,
  getUnitPriceForPriceBreak,
  isPartTypePricingCalculator,
} from 'helpers/parts'
import './partPivotTable.scss'

const getClass = prefix('partPivotTable-')

export const getCurrency = (part, currencies) => {
  if (currencies) {
    if (part.priceCurrencyId) {
      const ret = currencies.find(c => c.id === part.priceCurrencyId)
      if (ret) return ret
    }
    return currencies.find(c => c.isDefault)
  }
  return null
}

export const getTemplate = (part, partTemplates) => {
  if (partTemplates) return partTemplates.find(t => t.id === part.partTemplateId)
  return null
}

export const getTaxCode = (taxCodeId, taxCodes) => {
  if (taxCodes) return taxCodes.find(t => t.id === taxCodeId)
  return null
}

// Simple and comparable string representation of price summary for part
export const getPartPriceSummary = (part, pricingCurrency, isComplexPricingEnabled, companyId) => {
  if (part.packs) {
    const prices = {}
    const packs = []
    part.packs.forEach(pack => {
      const summary = getPackPriceSummary(part, pack, pricingCurrency, isComplexPricingEnabled, companyId)
      if (summary && summary.minPrice) {
        packs.push(pack.packTypeId)
        prices[pack.packTypeId] = summary
      }
    })
    packs.sort()
    return packs.map(packTypeId => {
      const price = prices[packTypeId]
      if (price) return `${packTypeId}:${price.minPrice}-${price.maxPrice}`
    }).join(',')
  }
  return ''
}

export const getPartCostSummary = (part) => {
  const partPacks = getPartPacks(part, isComplexPricingEnabled)
  const defaultPartPack = getDefaultPack(partPacks)

  return defaultPartPack ? `${defaultPartPack}:${defaultPartPack.standardCost}` : ''
}

// Price summary of part in properly rendered format
export const getRenderedPartPriceSummary = (
  part,
  packTypes,
  pricingCurrency,
  isComplexPricingEnabled,
  companyId
) => {
  if (part.packs) {
    return (
      <span>
        {part.packs.map((pack, index) => {
          const packType = packTypes ? packTypes.find(pt => pt.id === pack.packTypeId) : null
          const price = getRenderedPackPriceSummary(part, pack, pricingCurrency, isComplexPricingEnabled, companyId)
          if (price) {
            return (
              <span key={index}>
                {index > 0 ? ', ' : ''}{price}/{get(packType, 'description')}
              </span>
            )
          }
        })}
      </span>
    )
  }
  return null
}

const getDefaultPartPackCost = (defaultPartPack) => {
  const prices = get(defaultPartPack, 'prices', null)
  const basePriceBreakCost = get(getMinPriceBreak(prices), 'cost', null)

  if (isEmpty(prices) || isNil(basePriceBreakCost))
    return get(defaultPartPack, 'standardCost', 0)

  return basePriceBreakCost
}

export const getRenderedPartCostSummary = (part, packTypes, pricingCurrency, isComplexPricingEnabled) => {
  const partPacks = getPartPacks(part, isComplexPricingEnabled)
  const defaultPartPack = getDefaultPack(partPacks)

  if (isNil(defaultPartPack)) {
    return null
  }

  const packTypeId = get(defaultPartPack, 'packTypeId', null)
  const packType = getPackTypeFor(packTypeId, packTypes)

  return (
    <span>
      <FinancialString
        value={getDefaultPartPackCost(defaultPartPack)}
        currencyId={pricingCurrency.id}
        inline
      />
      {' '}/{get(packType, 'description')}
    </span>
  )
}

const getRenderedPackPriceSummary = (part, pack, pricingCurrency, isComplexPricingEnabled, companyId) => {
  const range = getPackPriceSummary(part, pack, pricingCurrency, isComplexPricingEnabled, companyId)
  if (range.minPrice) {
    if (range.maxPrice === range.minPrice) {
      return <PartPrice value={range.minPrice} part={part}/>
    }
    return (
      <span>
        <PartPrice value={range.minPrice} part={part}/> - <PartPrice value={range.maxPrice} part={part}/>
      </span>
    )
  }
  return null
}

const getPackPriceSummary = (part, pack, pricingCurrency, isComplexPricingEnabled, companyId) => {
  let minPrice = null, maxPrice = null

  const partPriceBreaks = getPartPriceBreaks(pack, isComplexPricingEnabled, companyId)
  if (partPriceBreaks) {
    partPriceBreaks.forEach(price => {
      const unitPrice = getUnitPriceForPriceBreak(part, pack, price, pricingCurrency)
      if (minPrice === null || unitPrice < minPrice) minPrice = unitPrice
      if (maxPrice === null || unitPrice > maxPrice) maxPrice = unitPrice
    })
  }

  return {minPrice, maxPrice}
}

const PartPrice = ({part, value}) => <FinancialString value={value} currencyId={part.priceCurrencyId} inline/>

PartPrice.propTypes = {
  part: propTypes.object.isRequired,
  value: propTypes.number.isRequired,
}

const getPricingCurrency = (part, currencies) => currencies.find(c => c.id === part.priceCurrencyId)

export const getColumnDefinition = (props) => {
  const {
    currencies,
    packTypes,
    partTemplates,
    taxCodes,
    nominalCodes,
    regionalLocaleRRPTerm,
    isComplexPricingEnabled,
    companyId,
  } = props

  const homeCurrency = getDefaultCurrency(currencies)

  return {
    status: {
      key: 'status',
      label: 'Status',
      size: 80,
      visible: true,
      order: 0,
      data: e => {
        const status = get(e, 'status', null)
        const statusColor = getColorForPartStatus(status)
        const label = get(getPartStatus(status), 'label', '')
        return getColorStatusOption(statusColor, label)
      },
      group: e => {
        const status = get(e, 'status', null)
        return getPartStatusLabel(status)
      },
      groupData: e => {
        const status = get(e, 'status', null)
        return getPartStatusLabel(status)
      },
      export: e => {
        const status = get(e, 'status', null)
        return get(getPartStatus(status), 'label', '')
      },
    },
    partCode: {
      key: 'partCode',
      label: 'Part Code',
      size: 60,
      visible: true,
      data: e => e.partCode,
      order: 1,
    },
    manCode: {
      key: 'manCode',
      label: 'M/F Code',
      size: 80,
      visible: false,
      data: e => get(e, 'manCode', null),
      order: 2,
    },
    description: {
      key: 'description',
      label: 'Description',
      size: 300,
      visible: true,
      data: e => e.description,
      order: 3,
    },
    template: {
      key: 'template',
      label: 'Type',
      size: 100,
      visible: true,
      data: e => get(getTemplate(e, partTemplates), 'name'),
      export: e => get(getTemplate(e, partTemplates), 'name'),
      order: 4,
    },
    category: {
      key: 'category',
      label: 'Category',
      size: 100,
      visible: true,
      data: e => get(e, 'category.name'),
      order: 5,
    },
    size: {
      key: 'size',
      label: 'Size',
      size: 50,
      visible: true,
      data: e => {
        if (isPartTypePricingCalculator(e.type)) return null
        let ret = ''
        if (e.sizeX) ret += e.sizeX
        if (e.sizeY) {
          if (ret.length) ret += 'x'
          ret += e.sizeY
        }
        if (e.sizeZ) {
          if (ret.length) ret += 'x'
          ret += e.sizeZ
        }
        return ret
      },
      order: 5,
    },
    price: {
      key: 'price',
      label: 'Price',
      size: 200,
      visible: true,
      data: e => isPartTypePricingCalculator(e.type) ? null : getRenderedPartPriceSummary(
        e,
        packTypes,
        getPricingCurrency(e, currencies),
        isComplexPricingEnabled,
        companyId
      ),
      group: e => getPartPriceSummary(
        e,
        getPricingCurrency(e, currencies),
        isComplexPricingEnabled,
        companyId
      ),
      export: e => isPartTypePricingCalculator(e.type) ? null : getPriceSummaryFor(
        e,
        packTypes,
        getPricingCurrency(e, currencies),
        isComplexPricingEnabled,
        companyId,
        currencies
      ),
      order: 6,
    },
    cost: {
      key: 'cost',
      label: 'Cost',
      size: 200,
      visible: true,
      data: e => isPartTypePricingCalculator(e.type) ? null : getRenderedPartCostSummary(e, packTypes, homeCurrency, isComplexPricingEnabled),
      group: e => getPartCostSummary(e),
      export: e => isPartTypePricingCalculator(e.type)
        ? null
        : getCostSummaryFor(e, packTypes, homeCurrency, isComplexPricingEnabled),
      order: 7,
    },
    taxCode: {
      key: 'taxCode',
      label: 'Tax Code',
      size: 40,
      visible: false,
      data: e => {
        const taxCode = getTaxCode(e.taxCodeId, taxCodes)
        return isNil(taxCode) ? null : get(getTaxCodeOption(taxCode), 'label', null)
      },
      order: 8,
    },
    type: {
      key: 'type',
      label: 'Part Type',
      size: 80,
      visible: false,
      data: e => get(getPartType(e.type), 'label', ''),
    },
    notes: {
      key: 'notes',
      label: 'Notes',
      size: 300,
      visible: false,
      data: e => e.notes,
    },
    privateNotes: {
      key: 'privateNotes',
      label: 'Sales Notes',
      size: 300,
      visible: false,
      data: e => e.privateNotes,
    },
    sizeX: {
      key: 'sizeX',
      label: 'Length',
      align: 'right',
      size: 30,
      visible: false,
      data: e => isPartTypePricingCalculator(e.type) ? null : getPartPropertyWithLengthClass(e, 'sizeX'),
    },
    sizeY: {
      key: 'sizeY',
      label: 'Width',
      align: 'right',
      size: 30,
      visible: false,
      data: e => isPartTypePricingCalculator(e.type) ? null : getPartPropertyWithLengthClass(e, 'sizeY'),
    },
    sizeZ: {
      key: 'sizeZ',
      label: 'Height',
      align: 'right',
      size: 30,
      visible: false,
      data: e => isPartTypePricingCalculator(e.type) ? null : getPartPropertyWithLengthClass(e, 'sizeZ'),
    },
    weight: {
      key: 'weight',
      label: 'Weight',
      align: 'right',
      size: 30,
      visible: false,
      data: e => getPartPropertyWithWeightClass(e),
    },
    nominalCode: {
      key: 'nominalCode',
      label: 'Sales Nominal Code',
      size: 80,
      visible: false,
      data: e => getNominalCodeDescription(e.nominalCode, nominalCodes),
    },
    purchaseNominalCode: {
      key: 'purchaseNominalCode',
      label: 'Purchase Nominal Code',
      size: 80,
      visible: false,
      data: e => isPartTypePricingCalculator(e.type)
        ? null
        : getNominalCodeDescription(e.purchaseNominalCode, nominalCodes),
    },
    msrpPricing: {
      key: 'msrpPricing',
      label: `Use ${regionalLocaleRRPTerm}`,
      size: 80,
      visible: false,
      data: e => {
        if (isPartTypePricingCalculator(e.type)) return null
        return e.msrpPricing ? 'YES' : 'NO'
      },
    },
    priceCurrency: {
      key: 'priceCurrency',
      label: 'Pricing Currency',
      size: 80,
      visible: false,
      data: e => get(getCurrency(e, currencies), 'shortDesc'),
    },
    createdDate: createDateTimeColumn({
      key: 'createdDate',
      label: 'Created',
      size: 80,
      visible: false,
      data: e => e.createdDate,
    }),
    modifiedDate: createDateTimeColumn({
      key: 'modifiedDate',
      label: 'Last Modified',
      size: 80,
      visible: false,
      data: e => e.modifiedDate,
    }),
  }
}

class PartPivotTable extends Component {
  render() {
    const {
      stateKey, showDormants, onSelectPart, onUpdateFilters, canSelectRows,
      areAllRowsSelected, onSelectAllRows, onDeSelectAllRows, onToggleSelectedRow,
      ...otherProps
    } = this.props
    const columns = getColumnDefinition(this.props)
    const controlColumns = getControlColumns(getClass)

    return (
      <PivotTable
        stateKey={stateKey}
        singularNoun="Part"
        pluralNoun="Parts"
        mode="grid"
        icon={iconNames.part}
        columns={columns}
        controlColumns={controlColumns}
        onSelectEntity={onSelectPart}
        className={getClass('pivot-table')}
        canSelectRows={canSelectRows}
        areAllRowsSelected={areAllRowsSelected}
        onSelectAllRows={onSelectAllRows}
        onDeSelectAllRows={onDeSelectAllRows}
        onToggleSelectedRow={onToggleSelectedRow}
        placeholderHeightStyle={'calc(100% - 198px)'}
        tightFilters
        {...otherProps}
      >
        <FormControlLabel
          className='toggle-inactive-parts'
          control={
            <Switch checked={showDormants} onChange={(e, s) => onUpdateFilters({showDormants: s})}/>
          }
          label="Show Discontinued / Inactive Parts"
        />
      </PivotTable>
    )
  }
}

PartPivotTable.propTypes = {
  stateKey: propTypes.string.isRequired,
  showDormants: propTypes.bool,
  onUpdateFilters: propTypes.func,
  onSelectPart: propTypes.func,
  currencies: propTypes.array.isRequired,
  packTypes: propTypes.array.isRequired,
  partTemplates: propTypes.array.isRequired,
  taxCodes: propTypes.array.isRequired,
  nominalCodes: propTypes.array.isRequired,
  regionalLocaleRRPTerm: propTypes.string.isRequired,
  isComplexPricingEnabled: propTypes.bool.isRequired,
  companyId: propTypes.number,
  canSelectRows: propTypes.bool,
  areAllRowsSelected: propTypes.bool,
  onSelectAllRows: propTypes.func,
  onDeSelectAllRows: propTypes.func,
  onToggleSelectedRow: propTypes.func,
}

PartPivotTable.defaultProps = {
  currencies: [],
  packTypes: [],
  partTemplates: [],
  taxCodes: [],
  nominalCodes: [],
  canSelectRows: false,
}

const mapStateToProps = (state) => ({
  currencies: getCurrencies(state),
  packTypes: getPackTypes(state),
  partTemplates: getPartTemplates(state),
  taxCodes: getTaxCodes(state),
  nominalCodes: getNominalCodes(state),
  regionalLocaleRRPTerm: getRegionalLocaleRRPTerm(state),
  isComplexPricingEnabled: isComplexPricingEnabled(state),
  areAllRowsSelected: getAreAllRowsSelected(state, partsPivotTableKey),
})

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    onSelectAllRows: () => dispatch(selectAllRows(ownProps.stateKey)),
    onDeSelectAllRows: () => dispatch(deSelectAllRows(ownProps.stateKey)),
    onToggleSelectedRow: (part, index) => dispatch(toggleSelectedEntity(ownProps.stateKey, part, index)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(PartPivotTable)
